import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Divider,
  Header,
  Loader,
  Pagination,
  Table
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import ProjectsHeader from "../../components/projects/ProjectsHeader.jsx";
import ProjectFilters from "../../components/projects/ProjectFilters.jsx";
import ProjectCard from "../../components/projects/ProjectCard.jsx";
import ProjectRowHeader from "../../components/projects/ProjectRowHeader.jsx";
import ProjectRow from "../../components/projects/ProjectRow.jsx";
import ChallengeConfirmationModal from "../../components/modals/ChallengeConfirmationModal.jsx";
import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";

import { PROJECTS } from "../../state/actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";

import { getDisabledStateFromMarking } from "../../utils/marking.js";
import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils.js";

import { getProjects } from "../../state/reducers/projects/projectsActions.js";
import { operationAction } from "../../state/reducers/operations/operationActions.js";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector.js";
import projectSelector from "../../state/reducers/projects/projectSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Projects extends Component {
  static numProjectCards = 0;
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    dataIndex: PropTypes.array,
    dataMap: PropTypes.object,
    totalPages: PropTypes.number,
    page: PropTypes.number,
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    listProjectAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    getProjects: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "projects"
  };

  constructor(props) {
    super(props);

    this.state = {
      filters: null,
      view: "card",
      redirect: null,

      showModal: false,
      modalCopyKey: "",
      modalData: {},
      modalAction: null
    };
  }

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    // do search params change
    if (this.props.location.search !== prevProps.location.search) {
      this.getData();
    }
  }

  getData = () => {
    const { view } = this.state;
    const {
      location,
      isFetching,
      isErrored,
      getProjects,
      listProjectAvailability: { available }
    } = this.props;

    if (!isFetching && !isErrored && available) {
      getProjects(location.search, view === "card", view === "card");
    }
  };

  // -- HANDLERS & ACTIONS -- //

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  handleDelete = id => {
    const { copyKey } = this.props;

    const modalCopyKey = `${copyKey}.modals.delete_modal`;
    const modalData = { id };
    const modalAction = this.doDelete;

    this.setState(
      {
        modalCopyKey,
        modalData,
        modalAction
      },
      this.openModal
    );
  };

  doDelete = () => {
    const { dataMap, operationAction } = this.props;
    const { modalData } = this.state;

    operationAction(dataMap[modalData.id], OPERATIONS.DELETE);
  };

  // -- MODAL -- //

  openModal = () => {
    this.setState({ showModal: true });
  };

  closeModal = confirmed => {
    const { modalAction } = this.state;

    if (confirmed === true) modalAction();

    this.setState({ showModal: false });
  };

  // -- RENDER -- //

  renderProjectCards = () => {
    const { dataMap, dataIndex } = this.props;

    if (dataIndex.length < 1 || !dataMap) return null;

    return dataIndex.map(id => {
      const project = dataMap[id];
      const disabled = Object.assign(
        {},
        getDisabledStateFromMarking(project.marking)
      );

      return (
        <ProjectCard
          key={id}
          id={id}
          project={project}
          disabled={disabled.functions}
          onDelete={this.handleDelete}
        />
      );
    });
  };

  renderProjectRows = () => {
    const { dataMap, dataIndex, copy } = this.props;

    if (!dataMap) return null;

    return dataIndex.map(id => {
      const project = dataMap[id];
      const disabled = Object.assign(
        {},
        getDisabledStateFromMarking(project.marking)
      );

      return (
        <ProjectRow
          key={id}
          onDelete={this.handleDelete}
          project={project}
          disabled={disabled.functions}
          copy={copy}
        />
      );
    });
  };

  renderProjectTable = () => {
    const { dataIndex, copy } = this.props;

    if (dataIndex.length < 1) {
      return null;
    }

    return (
      <Table celled basic striped unstackable>
        <ProjectRowHeader copy={copy} />
        <Table.Body>{this.renderProjectRows()}</Table.Body>
      </Table>
    );
  };

  render() {
    const { view, redirect, filters, modalData, modalCopyKey, showModal } =
      this.state;

    const {
      dataMap,
      dataIndex,
      page,
      totalPages,
      location,
      isFetching,
      listProjectAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (!permitted) {
      return <UnauthorisedFeature title={copy(`${copyKey}.title`)} />;
    }

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (isFetching) {
      return (
        <Container>
          <ProjectsHeader />

          <ProjectFilters
            onFilterChange={this.onFilterChange}
            initialValues={{ filters, view }}
          />

          <Header as="h3">{copy(`${copyKey}.title`)}</Header>
          <Loader active inline="centered" />
        </Container>
      );
    }

    return (
      <Container>
        <ProjectsHeader />

        <ProjectFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters, view }}
        />

        <Header as="h3">{copy(`${copyKey}.title`)}</Header>

        {(!enabled || !dataMap || !dataIndex || dataIndex.length < 1) && (
          <p>{copy(`${copyKey}.none_available`)}</p>
        )}

        {view === "card" && this.renderProjectCards()}
        {view === "list" && this.renderProjectTable()}

        {totalPages && totalPages > 1 && (
          <Fragment>
            <Pagination
              defaultActivePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right clear-both"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}

        {showModal && (
          <ChallengeConfirmationModal
            open={showModal}
            entity={dataMap[modalData.id]}
            onClose={this.closeModal}
            copyKey={modalCopyKey}
          />
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  dataIndex: state.projects.dataIndex,
  dataMap: projectSelector.selectProjectsDataMap(state),
  totalPages: state.projects.totalPages,
  page: state.projects.page,
  isFetching: state.projects.isFetching,
  isErrored: state.api.isError,
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProjects: (...args) => dispatch(getProjects(...args)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(PROJECTS, entity, operationName, data, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(Projects);
